/**
 * Bateau pirate (author's map, not in the GDD): a galleon of wood seen in section, on the sea between two islets, with
 * a main deck, a gun deck, a hold, a cabin under the poop and a raised forecastle, tied by ramps. Barrels of black
 * powder wait everywhere: the ship burns, the powder blows, and the hull that gives lets the sea in. Under the masts,
 * one walks through: they stand a little above the deck. Drawn: the seed changes nothing here.
 */
import { Physique } from '@invaders/sim';
import { plainMap, type MapState } from '../state';
import { type Gen, ellipse, rect, sea, settle, tree } from './common';

const { EMPTY, ROCK, SAND, WOOD, OBSIDIAN, BLACK_POWDER } = Physique;

const MID = 520;
const HALF = 310;
/** The rows of the decks, under the sea's row for the hold. */
const MAIN_ABOVE_SEA = 66;
const GUN_ABOVE_SEA = 34;
const HOLD_UNDER_SEA = 14;
const PLANK = 4;
const POOP_ROW = 286;
const FORE_ROW = 300;
export const KEG_W = 12;
export const KEG_H = 14;

/** A keg standing on row `floor`: staves of wood around black powder. */
function keg(g: Gen, x: number, floor: number): void {
  rect(g, x, floor - KEG_H, x + KEG_W - 1, floor - 1, WOOD);
  rect(g, x + 1, floor - KEG_H + 1, x + KEG_W - 2, floor - 2, BLACK_POWDER);
}

/** A ramp going down at 45° from (x, y) in direction `dir`, until something stops it. */
function ramp(g: Gen, x: number, y: number, dir: number): void {
  for (let k = 0; k < 90; k++) {
    if (g.data[(y + k + PLANK - 1) * g.w + x + dir * k] !== EMPTY) return;
    rect(g, x + dir * k, y + k, x + dir * k, y + k + PLANK - 1, WOOD, EMPTY);
  }
}

/** A sail: rows of wood one row apart, seams to hold them together. */
function sail(g: Gen, x: number, y0: number, y1: number, half: number): void {
  for (let y = y0; y <= y1; y += 2) rect(g, x - half, y, x + half, y, WOOD);
  for (let c = x - half; c <= x + half; c += 14) rect(g, c, y0, c, y1, WOOD);
  rect(g, x - half - 8, y0 - 3, x + half + 8, y0 - 1, WOOD); // the yard
}

export function drawPirate(g: Gen): MapState {
  const { w, h, seaY } = g;
  const main = seaY - MAIN_ABOVE_SEA;
  const gun = seaY - GUN_ABOVE_SEA;
  const hold = seaY + HOLD_UNDER_SEA;
  const sq = (x: number) => (x - MID) * (x - MID);
  const top = (x: number) => main - Math.trunc((16 * sq(x)) / (HALF * HALF));
  const keel = (x: number) => seaY + 40 - Math.trunc((110 * sq(x)) / (HALF * HALF));

  // The sea floor and the two islets.
  rect(g, 0, seaY + 72, w - 1, h - 1, ROCK);
  for (const c of [100, 930]) {
    ellipse(g, c, seaY + 34, 66, 62, ROCK);
    ellipse(g, c, seaY + 30, 54, 62, SAND, EMPTY);
  }
  sea(g);

  // The hull, on the sea: the planks, then the air inside.
  for (let x = MID - HALF; x <= MID + HALF; x++) rect(g, x, top(x), x, keel(x), WOOD);
  for (let x = MID - HALF; x <= MID + HALF; x++) rect(g, x, top(x) + PLANK, x, keel(x) - PLANK - 1, EMPTY);
  for (let x = MID - HALF; x <= MID + HALF; x++) {
    if (keel(x) > gun + 2 * PLANK) rect(g, x, gun, x, gun + PLANK - 1, WOOD, EMPTY);
    if (keel(x) > hold + 2 * PLANK) rect(g, x, hold, x, hold + PLANK - 1, WOOD, EMPTY);
  }

  // Hatches and ramps: main deck to gun deck, gun deck to hold.
  rect(g, 440, main - 2, 478, main + PLANK + 2, EMPTY);
  ramp(g, 478, main + 1, -1);
  rect(g, 560, gun, 612, gun + PLANK - 1, EMPTY);
  ramp(g, 612, gun + 1, -1);

  // The poop: a cabin with a door to the main deck, a ramp inside up to a hatch in its roof.
  rect(g, 226, POOP_ROW, 330, POOP_ROW + PLANK - 1, WOOD);
  rect(g, 226, POOP_ROW, 230, top(230), WOOD);
  rect(g, 326, POOP_ROW, 330, top(330), WOOD);
  rect(g, 326, top(330) - 24, 330, top(330) - 1, EMPTY);
  rect(g, 268, POOP_ROW, 308, POOP_ROW + PLANK - 1, EMPTY);
  ramp(g, 268, POOP_ROW + 1, 1);

  // The forecastle: a raised deck on posts, a ramp up to it.
  rect(g, 696, FORE_ROW, MID + HALF - 26, FORE_ROW + PLANK - 1, WOOD, EMPTY);
  for (let x = 696; x < MID + HALF - 30; x += 36) rect(g, x, FORE_ROW, x + 1, top(x), WOOD, EMPTY);
  ramp(g, 696, FORE_ROW + 1, -1);

  // Masts and sails. A mast stops 20 rows above the deck, so that one walks under it.
  for (const [x, peak] of [[372, 96], [540, 70], [742, 110]]) {
    const foot = (x > 690 ? FORE_ROW : top(x)) - 20;
    rect(g, x - 2, peak, x + 3, foot, WOOD);
    rect(g, x - 7, foot - 1, x + 8, foot, WOOD);
    sail(g, x, peak + 18, peak + 62, 44);
    if (foot > peak + 110) sail(g, x, peak + 86, peak + 126, 56);
    rect(g, x - 12, peak + 8, x + 13, peak + 10, WOOD); // the crow's nest
  }

  // Cannons on the gun deck: too low to stop a walker.
  for (let x = 300; x <= 720; x += 96) if (x < 540 || x > 620) rect(g, x, gun - 5, x + 18, gun - 1, OBSIDIAN);

  // The kegs.
  for (const x of [392, 410, 500, 640]) keg(g, x, top(x));
  for (const x of [262, 346, 400, 500, 636, 668]) keg(g, x, gun);
  for (const x of [380, 394, 408, 422, 470, 484, 620, 650]) keg(g, x, hold);
  keg(g, 244, top(250));
  keg(g, 760, FORE_ROW);

  // The islets: a keg and a tree each.
  for (const c of [100, 930]) {
    let y = 0;
    while (g.data[y * w + c] === EMPTY) y++;
    tree(g, c - 20, y + 2, 30);
    keg(g, c + 18, y + 2);
  }
  settle(g);
  return plainMap('pirate');
}
